/**
 * Lightweight Real-User Monitoring — fires three event types:
 *   - page_load            on every route change (Navigation Timing v2)
 *   - check_completion     when "Check safety" → result render completes
 *   - shared_page_viewed   on /r/{share_id} mount
 *
 * Zero-impact on UX: every event is fire-and-forget, swallowed on failure.
 * The backend `/api/events` endpoint accepts these names (allow-listed).
 *
 * Sampling: 100% in production launch week so we have signal; can dial
 * back via REACT_APP_RUM_SAMPLE_RATE env var (0.0–1.0) post-launch.
 */
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { trackEvent } from "./api";

const SAMPLE_RATE = Math.max(
  0,
  Math.min(1, Number(process.env.REACT_APP_RUM_SAMPLE_RATE ?? 1)),
);

function shouldSample() {
  return Math.random() < SAMPLE_RATE;
}

function viewportLabel() {
  if (typeof window === "undefined") return "ssr";
  const w = window.innerWidth || 0;
  if (w < 480) return "mobile";
  if (w < 1024) return "tablet";
  return "desktop";
}

function referrerKind() {
  if (typeof document === "undefined") return "unknown";
  const r = document.referrer || "";
  if (!r) return "direct";
  try {
    const host = new URL(r).hostname.toLowerCase();
    if (host.includes("google.")) return "google";
    if (host.includes("bing.")) return "bing";
    if (host.includes("twitter.") || host === "t.co") return "twitter";
    if (host.includes("facebook.") || host === "fb.me") return "facebook";
    if (host.includes("instagram.")) return "instagram";
    if (host.includes("reddit.")) return "reddit";
    if (host.includes(window.location.hostname)) return "internal";
    return "external";
  } catch {
    return "unknown";
  }
}

/** Fire-and-forget page_load event on every route change. */
export function usePageLoadRum() {
  const { pathname } = useLocation();
  const firstLoadRef = useRef(true);

  useEffect(() => {
    if (!shouldSample()) return;
    // For the FIRST render we use Navigation Timing v2 to get a real
    // "user-perceived load time"; for subsequent SPA route changes we
    // use performance.now() — close enough for funnel signal.
    let durationMs = null;
    if (firstLoadRef.current && typeof performance !== "undefined") {
      const nav = performance.getEntriesByType?.("navigation")?.[0];
      if (nav && typeof nav.loadEventEnd === "number" && nav.loadEventEnd > 0) {
        durationMs = Math.round(nav.loadEventEnd - nav.startTime);
      }
      firstLoadRef.current = false;
    }
    // Defer slightly so SPA route transitions have a chance to render.
    const tStart = performance.now();
    const raf = requestAnimationFrame(() => {
      const measured = durationMs != null
        ? durationMs
        : Math.max(0, Math.round(performance.now() - tStart));
      // Normalize the path so we don't fragment by share-id or product slug.
      const path = pathname
        .replace(/^\/r\/[^/]+/, "/r/:id")
        .replace(/^\/products\/[^/]+/, "/products/:slug")
        .replace(/^\/medications\/[^/]+/, "/medications/:slug")
        .replace(/^\/ingredients\/[^/]+/, "/ingredients/:slug");
      trackEvent("page_load", {
        path,
        duration_ms: measured,
        viewport:     viewportLabel(),
        referrer_kind: referrerKind(),
      });
    });
    return () => cancelAnimationFrame(raf);
  }, [pathname]);
}

/** Track a single check-safety click → result render duration.
 *
 *    const checkTimer = useCheckTimer();
 *    function onClickCheck() { checkTimer.start(); ... }
 *    // when the verdict is rendered:
 *    checkTimer.finish({ verdict, score, from_share_id });
 */
export function useCheckTimer() {
  const startTsRef = useRef(null);
  return {
    start() {
      startTsRef.current = performance.now();
    },
    finish(extra = {}) {
      const ts = startTsRef.current;
      if (ts == null || !shouldSample()) return;
      startTsRef.current = null;
      const durationMs = Math.max(0, Math.round(performance.now() - ts));
      trackEvent("check_completion", {
        duration_ms: durationMs,
        viewport:    viewportLabel(),
        ...extra,
      });
    },
  };
}

/** Fire `shared_page_viewed` exactly once when /r/{id} mounts with data. */
export function emitSharedPageViewed(shareId, extra = {}) {
  if (!shareId || !shouldSample()) return;
  trackEvent("shared_page_viewed", {
    share_id: shareId,
    viewport: viewportLabel(),
    referrer_kind: referrerKind(),
    ...extra,
  });
}
